import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Check, FileText, LoaderCircle, X } from "lucide-react";
import type { OpsAssistantArtifact } from "@business-ops/domain";
import { installDrawerFocusBoundary } from "./ui.js";
import "./ops-document-artifact.css";

const OpsPdfViewer = lazy(async () => {
  const module = await import("./ops-pdf-viewer.js");
  return { default: module.OpsPdfViewer };
});

const MAX_ARTIFACT_BYTES = 2 * 1024 * 1024;

type ArtifactBytes = Uint8Array<ArrayBuffer>;
type ArtifactViewerMode = "PDF" | "TEXT";
type ArtifactLoadState =
  | { status: "idle" | "loading" }
  | { status: "ready"; text?: string; bytes?: ArtifactBytes; blobUrl?: string };

function artifactByteBuffer(bytes: ArtifactBytes): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

function artifactOrigin(): string {
  return typeof window === "undefined" ? "http://localhost" : window.location.origin;
}

/** Reject external/credential-bearing URLs before the authenticated fetch. */
export function opsArtifactDownloadUrl(downloadUrl: string, origin = artifactOrigin()): string | null {
  try {
    const url = new URL(downloadUrl, origin);
    if (!/^https?:$/u.test(url.protocol) || url.origin !== origin || url.username || url.password) return null;
    // Keep the server-issued relative route unchanged so cookies, CSP, and
    // reverse-proxy routing remain owned by the current Chat origin.
    return downloadUrl.startsWith("/") && !downloadUrl.startsWith("//") ? downloadUrl : url.href;
  } catch {
    return null;
  }
}

export async function opsArtifactSha256(bytes: ArtifactBytes): Promise<string> {
  if (!globalThis.crypto?.subtle) throw new Error("artifact_integrity_unavailable");
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("");
}

async function readBoundedResponse(response: Response, expectedBytes: number, signal: AbortSignal): Promise<ArtifactBytes> {
  if (!response.ok) throw new Error("artifact_unavailable");
  if (!Number.isSafeInteger(expectedBytes) || expectedBytes < 1 || expectedBytes > MAX_ARTIFACT_BYTES) {
    await response.body?.cancel();
    throw new Error("artifact_size_invalid");
  }
  const declared = response.headers.get("content-length");
  if (declared !== null && Number(declared) !== expectedBytes) {
    await response.body?.cancel();
    throw new Error("artifact_size_mismatch");
  }
  const reader = response.body?.getReader();
  if (!reader) {
    const fallback = new Uint8Array(await response.arrayBuffer());
    if (fallback.byteLength !== expectedBytes) throw new Error("artifact_size_mismatch");
    return fallback;
  }
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    for (;;) {
      signal.throwIfAborted();
      const next = await reader.read();
      if (next.done) break;
      total += next.value.byteLength;
      if (total > expectedBytes) {
        await reader.cancel();
        throw new Error("artifact_size_mismatch");
      }
      chunks.push(next.value);
    }
  } finally {
    reader.releaseLock();
  }
  if (total !== expectedBytes) throw new Error("artifact_size_mismatch");
  const bytes = new Uint8Array(total) as ArtifactBytes;
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return bytes;
}

export async function loadOpsArtifactBytes(artifact: OpsAssistantArtifact, signal: AbortSignal): Promise<ArtifactBytes> {
  signal.throwIfAborted();
  const url = opsArtifactDownloadUrl(artifact.downloadUrl);
  if (!url) throw new Error("artifact_url_invalid");
  const response = await fetch(url, {
    credentials: "same-origin",
    cache: "no-store",
    redirect: "error",
    signal
  });
  const bytes = await readBoundedResponse(response, artifact.byteCount, signal);
  signal.throwIfAborted();
  if (await opsArtifactSha256(bytes) !== artifact.sha256) throw new Error("artifact_integrity_mismatch");
  signal.throwIfAborted();
  return bytes;
}

function artifactBlobUrl(artifact: OpsAssistantArtifact, bytes: ArtifactBytes): string {
  return URL.createObjectURL(new Blob([artifactByteBuffer(bytes)], { type: artifact.mimeType }));
}

function artifactErrorText(error: unknown): string {
  if (error instanceof Error && error.message === "artifact_integrity_mismatch") return "The downloaded bytes do not match this immutable artifact.";
  if (error instanceof Error && error.message === "artifact_size_mismatch") return "The downloaded file was incomplete or changed size.";
  if (error instanceof Error && error.message === "artifact_integrity_unavailable") return "This browser cannot verify the artifact before opening it.";
  return "The artifact could not be opened. Use Download to retry.";
}

function OpsInlineDocumentPreview({ artifact, onOpen }: { artifact: OpsAssistantArtifact; onOpen: () => void }) {
  const [bytes, setBytes] = useState<ArtifactBytes | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const isPdf = artifact.mimeType === "application/pdf";

  useEffect(() => {
    const target = previewRef.current;
    if (!target) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    // Long chat history must not download/render every old PDF at once.
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const controller = new AbortController();
    let active = true;
    void loadOpsArtifactBytes(artifact, controller.signal).then((verifiedBytes) => {
      if (!active || controller.signal.aborted) return;
      if (!isPdf) setText(new TextDecoder("utf-8", { fatal: true }).decode(verifiedBytes).slice(0, 4000));
      setBytes(verifiedBytes);
    }).catch(() => {
      if (active && !controller.signal.aborted) setFailed(true);
    });
    return () => { active = false; controller.abort(); };
  }, [visible, artifact.id, artifact.sha256, artifact.byteCount, artifact.downloadUrl, isPdf]);

  return <div className="ops-document-artifact-card__preview" ref={previewRef}>
    {bytes ? <>
      {isPdf ? <Suspense fallback={<p role="status">Loading preview…</p>}><OpsPdfViewer bytes={bytes} label={artifact.displayName} thumbnail /></Suspense> : <>
        {/* Authored file bytes may differ from the model's preview description.
            Render only verified literal text, never active HTML or that summary. */}
        <pre className="ops-document-artifact-card__text-preview">{text}</pre>
        <small className="ops-document-artifact-card__text-caption">Tap to read full file</small>
      </>}
      <button type="button" className="ops-document-artifact-card__preview-open" onClick={onOpen} aria-label={`Open ${artifact.displayName} full size`} />
    </> : <p role="status">{failed ? "Preview unavailable. Open the file below." : "Loading preview…"}</p>}
  </div>;
}

export function OpsDocumentArtifactCard({ artifact, privateMode = false }: { artifact: OpsAssistantArtifact; privateMode?: boolean }) {
  const [viewer, setViewer] = useState<ArtifactViewerMode | null>(null);
  const [loadState, setLoadState] = useState<ArtifactLoadState>({ status: "idle" });
  const [error, setError] = useState<string | null>(null);
  const [downloadState, setDownloadState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const panelRef = useRef<HTMLElement>(null);
  const downloadControllerRef = useRef<AbortController | null>(null);
  const viewerHistoryRef = useRef(false);
  const viewerCloseRequestedRef = useRef(false);
  const viewerCloseRef = useRef(() => setViewer(null));
  viewerCloseRef.current = () => setViewer(null);

  useEffect(() => () => downloadControllerRef.current?.abort(), []);
  useEffect(() => {
    if (!privateMode) return;
    downloadControllerRef.current?.abort();
    downloadControllerRef.current = null;
    setDownloadState("idle");
    setViewer(null);
  }, [privateMode]);

  useEffect(() => {
    if (!viewer) return;
    const controller = new AbortController();
    let createdUrl: string | null = null;
    setLoadState({ status: "loading" });
    setError(null);
    void loadOpsArtifactBytes(artifact, controller.signal).then((bytes) => {
      if (viewer === "PDF") {
        createdUrl = artifactBlobUrl(artifact, bytes);
        setLoadState({ status: "ready", bytes, blobUrl: createdUrl });
        return;
      }
      const text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
      setLoadState({ status: "ready", text });
    }).catch((reason: unknown) => {
      if (!controller.signal.aborted) {
        setLoadState({ status: "idle" });
        setError(artifactErrorText(reason));
      }
    });
    return () => {
      controller.abort();
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [artifact.byteCount, artifact.downloadUrl, artifact.id, artifact.mimeType, artifact.sha256, viewer]);

  useEffect(() => {
    if (!viewer) return;
    return installDrawerFocusBoundary(panelRef.current, () => viewerCloseRef.current());
  }, [viewer]);

  useEffect(() => {
    if (!viewer) return;
    // Keep the transient viewer on the current Chat route. A device Back
    // consumes this same-document entry first, then closes the viewer.
    window.history.pushState({ ...(window.history.state ?? {}), opsDocumentArtifact: artifact.id }, "", window.location.href);
    viewerHistoryRef.current = true;
    viewerCloseRequestedRef.current = false;
    const onPopState = () => {
      viewerHistoryRef.current = false;
      viewerCloseRequestedRef.current = false;
      viewerCloseRef.current();
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      if (viewerHistoryRef.current && !viewerCloseRequestedRef.current) {
        viewerHistoryRef.current = false;
        window.history.back();
      }
    };
  }, [artifact.id, viewer]);

  const download = async () => {
    setDownloadState("loading");
    const controller = new AbortController();
    downloadControllerRef.current?.abort();
    downloadControllerRef.current = controller;
    try {
      const bytes = await loadOpsArtifactBytes(artifact, controller.signal);
      const href = artifactBlobUrl(artifact, bytes);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = artifact.displayName;
      anchor.click();
      URL.revokeObjectURL(href);
      setDownloadState("done");
    } catch {
      setDownloadState(controller.signal.aborted ? "idle" : "error");
    } finally {
      if (downloadControllerRef.current === controller) downloadControllerRef.current = null;
    }
  };

  const openViewer = (mode: ArtifactViewerMode) => {
    setError(null);
    setViewer(mode);
  };
  const closeViewer = () => {
    if (viewerHistoryRef.current) {
      if (!viewerCloseRequestedRef.current) {
        viewerCloseRequestedRef.current = true;
        window.history.back();
      }
      return;
    }
    setViewer(null);
  };
  const isText = artifact.mimeType !== "application/pdf";

  return <>
    <article className="ops-document-artifact-card" data-artifact-id={artifact.id}>
      <header>
        <span className="ops-document-artifact-card__title"><FileText size={16} aria-hidden="true" /><span><small>{artifact.kind === "SHEET" ? "Spreadsheet" : "Document"}</small><strong>{artifact.displayName}</strong></span></span>
        <small>{formatArtifactBytes(artifact.byteCount)}</small>
      </header>
      {/* WHY [BOL-ARTIFACT-INLINE-001]: the real PDF already worked in its
          popup, but Chat showed only a prose description. Show the same
          verified page scaled to fit, and keep the accepted popup unchanged.
          Pixels cannot inherit contact masking: Private removes this child
          before paint and aborts its work. Identity keys prevent stale revisions. */}
      {!privateMode ? <OpsInlineDocumentPreview key={`${artifact.id}:${artifact.sha256}:${artifact.byteCount}:${artifact.mimeType}:${artifact.downloadUrl}`} artifact={artifact} onOpen={() => openViewer(isText ? "TEXT" : "PDF")} /> : null}
      <pre>{artifact.preview.text}</pre>
      {artifact.preview.truncated ? <small className="ops-document-artifact-card__hint">Preview shortened. Read the verified export in Chat.</small> : null}
      <div className="ops-document-artifact-card__actions">
        {!privateMode ? <button type="button" onClick={() => openViewer(isText ? "TEXT" : "PDF")}><span>{isText ? "Read full file" : "View PDF"}</span><ArrowRight size={15} aria-hidden="true" /></button> : null}
        <button type="button" onClick={() => void download()} disabled={downloadState === "loading"}>
          {downloadState === "loading" ? <LoaderCircle size={15} aria-hidden="true" className="ops-document-artifact-card__spin" /> : downloadState === "done" ? <Check size={15} aria-hidden="true" /> : null}
          <span>{downloadState === "loading" ? "Verifying…" : downloadState === "done" ? "Downloaded" : "Download"}</span>
        </button>
      </div>
      {privateMode ? <small className="ops-document-artifact-card__private">Full file viewing is unavailable in Private mode.</small> : null}
      {downloadState === "error" ? <p className="ops-document-artifact-card__error" role="alert">The download could not be verified.</p> : null}
    </article>
    {viewer ? <div className="ops-document-viewer" onPointerDown={(event) => { if (event.target === event.currentTarget) closeViewer(); }}>
      <section className="ops-document-viewer__panel" ref={panelRef} role="dialog" aria-modal="true" aria-label={`${artifact.displayName} viewer`} tabIndex={-1}>
        <header className="ops-document-viewer__header">
          <button type="button" onClick={closeViewer}><ArrowLeft size={17} aria-hidden="true" />Back</button>
          <strong>{artifact.displayName}</strong>
          <button type="button" onClick={closeViewer} aria-label="Close artifact viewer"><X size={18} aria-hidden="true" /></button>
        </header>
        <div className="ops-document-viewer__body" aria-busy={loadState.status === "loading"}>
          {loadState.status === "loading" ? <p className="ops-document-viewer__status" role="status">Verifying and opening…</p> : null}
          {error ? <p className="ops-document-viewer__status ops-document-viewer__status--error" role="alert">{error}</p> : null}
          {loadState.status === "ready" && viewer === "TEXT" && loadState.text !== undefined ? <pre className="ops-document-viewer__text">{loadState.text}</pre> : null}
          {/* WHY [BOL-OPS-PDF-RENDERER-001]: Chrome on the target Samsung showed
              a broken-document icon for the verified blob iframe. Native PDF
              iframe handling is therefore not the inline rendering contract.
              Render a private copy of the already size/SHA-verified bytes with
              bundled PDF.js; keep the blob URL only for explicit fallback. */}
          {loadState.status === "ready" && viewer === "PDF" && loadState.bytes ? <Suspense fallback={<p className="ops-document-viewer__status" role="status">Opening PDF renderer…</p>}><OpsPdfViewer bytes={loadState.bytes} label={artifact.displayName} /></Suspense> : null}
        </div>
        <footer className="ops-document-viewer__footer">
          <button type="button" onClick={() => void download()} disabled={downloadState === "loading"}>Download verified file</button>
          {viewer === "PDF" && loadState.status === "ready" && loadState.blobUrl ? <a href={loadState.blobUrl} target="_blank" rel="noopener noreferrer">Open in new tab</a> : null}
          {viewer === "PDF" ? <small>Verified PDF. If the inline view is unavailable, use Open in new tab or Download.</small> : <small>Full file verified before reading.</small>}
        </footer>
      </section>
    </div> : null}
  </>;
}

function formatArtifactBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
